/**
 * PacketTransferDialog — Wave 3 + Phase 19
 * --------------------------------------------------------------------------
 * Two modes:
 *
 *   1) Single-source mode (when `defaultSourcePacketId` is passed) —
 *      triggered from Packet Detail pages. Behavior UNCHANGED from Wave 3.
 *      Uses endpoint:
 *        POST /api/{material}/packets/{sourceId}/transfer
 *
 *   2) Multi-source mode (when `defaultSourcePacketId` is NOT passed) —
 *      triggered from the Raw Materials list page (Phase 19). Lets user
 *      select multiple purchased source packets sharing the SAME
 *      classification, and merge them into a single target packet
 *      (existing OR newly created inline).
 *      Uses endpoint:
 *        POST /api/{material}/packets/transfer-multi
 *
 *   Atomicity in multi-source mode is enforced server-side via Compare-and-
 *   Swap (CAS) — if any source has insufficient balance at commit-time the
 *   entire batch is rejected and rolled back. Frontend additionally
 *   prevents negative balances by disabling submit when any row exceeds
 *   its source's available balance.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { formatCarats, formatINR } from '@/lib/format';
// Wave 3.49 — portal-wide carat/piece validation (Rule 1 + Rule 2).
import { validateCaratMovement, validateMovementQty, assertStateConsistent } from '@/lib/caratValidation';
import { Plus, ArrowRightLeft, Trash2 } from 'lucide-react';

// Map material → API base + label
const MATERIAL_META = {
  diamond:     { base: '/diamonds',     label: 'Diamond Packet',     testidScope: 'diamond' },
  color_stone: { base: '/color-stones', label: 'Colour Stone Packet', testidScope: 'color-stone' },
  pearl:       { base: '/pearls',       label: 'Pearl Packet',        testidScope: 'pearl' },
};

const emptyNewPacket = () => ({
  classification_id: '',
  description: '',
  valuation_mode: 'fixed',
  fixed_rate_per_ct: '',
});

const emptySourceRow = () => ({
  // local row id (NOT packet_id) for stable React keys
  _rid: `r-${Math.random().toString(36).slice(2, 9)}`,
  packet_id: '',
  carats: '',
  pieces: '',
});

export default function PacketTransferDialog({
  open,
  onOpenChange,
  material,          // 'diamond' | 'color_stone' | 'pearl'
  companyId,
  defaultSourcePacketId = null,
  onTransferred,
}) {
  const meta = MATERIAL_META[material];
  const isMultiMode = !defaultSourcePacketId; // List page → multi-source UI

  const [allPackets, setAllPackets] = useState([]);
  const [classifications, setClassifications] = useState([]);

  // ----- Single-source state (UNCHANGED from Wave 3) -----
  const [sourceId, setSourceId] = useState(defaultSourcePacketId || '');
  const [carats, setCarats] = useState('');
  const [pieces, setPieces] = useState('');

  // ----- Multi-source state (Phase 19) -----
  const [sourceRows, setSourceRows] = useState([emptySourceRow()]);

  // ----- Target/shared state -----
  const [targetMode, setTargetMode] = useState('existing'); // 'existing' | 'new'
  const [targetId, setTargetId] = useState('');
  const [newPacket, setNewPacket] = useState(emptyNewPacket());
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Load packets + classifications when dialog opens
  useEffect(() => {
    if (!open || !companyId || !meta) return;
    (async () => {
      try {
        const [pkts, cls] = await Promise.all([
          api.get(`${meta.base}/packets`, { params: { company_id: companyId } }),
          api.get(`${meta.base}/classifications`, { params: { company_id: companyId } }),
        ]);
        setAllPackets(pkts.data || []);
        setClassifications(cls.data || []);
      } catch (e) {
        toast.error(extractError(e, 'Failed to load packets'));
      }
    })();
  }, [open, companyId, meta]);

  // Reset state whenever the dialog re-opens
  useEffect(() => {
    if (open) {
      setSourceId(defaultSourcePacketId || '');
      setSourceRows([emptySourceRow()]);
      setTargetMode('existing');
      setTargetId('');
      setCarats('');
      setPieces('');
      setNote('');
      setNewPacket(emptyNewPacket());
    }
  }, [open, defaultSourcePacketId]);

  // -------- Source candidates (purchased + non-zero balance) --------
  const sourceCandidates = useMemo(
    () => (allPackets || []).filter(
      (p) => p.purchase_status !== 'on_approval' && (p.balance_carats > 0 || p.balance_pieces > 0)
    ),
    [allPackets]
  );

  // ============================ SINGLE-SOURCE MODE ============================
  const singleSourcePacket = useMemo(
    () => sourceCandidates.find((p) => p.id === sourceId),
    [sourceCandidates, sourceId]
  );

  const singleTargetCandidates = useMemo(() => {
    if (!singleSourcePacket) return [];
    return (allPackets || []).filter(
      (p) =>
        p.id !== singleSourcePacket.id &&
        p.purchase_status === 'purchased' &&
        p.classification_id === singleSourcePacket.classification_id
    );
  }, [allPackets, singleSourcePacket]);

  // ============================ MULTI-SOURCE MODE ============================
  // The first row's selected packet locks the classification for all other rows.
  const lockedClassificationId = useMemo(() => {
    const first = sourceRows.find((r) => r.packet_id);
    if (!first) return null;
    const p = sourceCandidates.find((q) => q.id === first.packet_id);
    return p ? p.classification_id : null;
  }, [sourceRows, sourceCandidates]);

  const selectedSourceIds = useMemo(
    () => new Set(sourceRows.map((r) => r.packet_id).filter(Boolean)),
    [sourceRows]
  );

  // For a given row index, build the eligible packet options.
  // Eligibility: purchased + has-balance + same classification as the locked one
  // (if any) + not already selected in *another* row.
  const rowCandidates = (rid) => {
    return sourceCandidates.filter((p) => {
      if (lockedClassificationId && p.classification_id !== lockedClassificationId) return false;
      // exclude packets selected in OTHER rows
      const ownRow = sourceRows.find((r) => r._rid === rid);
      if (ownRow && p.id === ownRow.packet_id) return true; // keep own selection visible
      if (selectedSourceIds.has(p.id)) return false;
      return true;
    });
  };

  const multiTargetCandidates = useMemo(() => {
    if (!lockedClassificationId) return [];
    return (allPackets || []).filter(
      (p) =>
        !selectedSourceIds.has(p.id) &&
        p.purchase_status === 'purchased' &&
        p.classification_id === lockedClassificationId
    );
  }, [allPackets, lockedClassificationId, selectedSourceIds]);

  // Per-row validation
  const validatedRows = useMemo(() => {
    return sourceRows.map((row) => {
      const pkt = sourceCandidates.find((p) => p.id === row.packet_id);
      const ct = Number(row.carats) || 0;
      const pcs = Number(row.pieces) || 0;
      const errors = [];
      if (!pkt) {
        errors.push('Pick a source packet');
      } else {
        // Wave 3.49 Rule 1 + Rule 2 — gathers all violations as a single
        // friendly per-row error so the operator can see exactly why the
        // batch is blocked before they hit Submit.
        const ruleErr = validateCaratMovement({
          txnCarats: ct, txnPieces: pcs,
          availableCarats: pkt.balance_carats || 0,
          availablePieces: pkt.balance_pieces || 0,
          packetNo: pkt.packet_no,
          context: 'multi-source transfer line',
        });
        if (ruleErr) errors.push(ruleErr);
      }
      return { ...row, packet: pkt, ct, pcs, errors, valid: errors.length === 0 };
    });
  }, [sourceRows, sourceCandidates]);

  // Aggregated totals (multi mode)
  const totals = useMemo(() => {
    return validatedRows.reduce((acc, r) => {
      if (r.valid && r.packet) {
        acc.ct += r.ct;
        acc.pcs += r.pcs;
        acc.value += r.ct * (r.packet.current_rate_per_ct || 0);
      }
      return acc;
    }, { ct: 0, pcs: 0, value: 0 });
  }, [validatedRows]);

  // Final submit-eligibility
  const allRowsValid = validatedRows.every((r) => r.valid);
  const anyRowFilled = validatedRows.some((r) => r.packet);

  // -------- Row manipulation --------
  // Sanitize rows: when the locked classification changes (because the first
  // selected packet changed), clear OTHER rows whose packets no longer match.
  const sanitizeRows = (rows) => {
    const firstRow = rows.find((r) => r.packet_id);
    if (!firstRow) return rows;
    const lockPkt = sourceCandidates.find((p) => p.id === firstRow.packet_id);
    const lockClsId = lockPkt ? lockPkt.classification_id : null;
    if (!lockClsId) return rows;
    return rows.map((r) => {
      if (!r.packet_id || r._rid === firstRow._rid) return r;
      const p = sourceCandidates.find((q) => q.id === r.packet_id);
      if (p && p.classification_id !== lockClsId) {
        return { ...r, packet_id: '', carats: '', pieces: '' };
      }
      return r;
    });
  };

  const addRow = () => setSourceRows((prev) => sanitizeRows([...prev, emptySourceRow()]));
  const removeRow = (rid) =>
    setSourceRows((prev) =>
      prev.length <= 1 ? prev : sanitizeRows(prev.filter((r) => r._rid !== rid))
    );
  const updateRow = (rid, patch) =>
    setSourceRows((prev) => sanitizeRows(prev.map((r) => (r._rid === rid ? { ...r, ...patch } : r))));

  // Clear target when user picks an incompatible classification by changing rows
  const setTargetIdSafe = (id) => {
    if (!id) { setTargetId(''); return; }
    const t = (allPackets || []).find((p) => p.id === id);
    if (t && lockedClassificationId && t.classification_id !== lockedClassificationId) {
      setTargetId('');
      return;
    }
    setTargetId(id);
  };

  // If the locked classification changes and the currently-selected target no
  // longer matches, derive the effective target value at RENDER time (don't
  // setState in effect). The Select will simply not display the stale value.
  const effectiveTargetId = useMemo(() => {
    if (!targetId) return '';
    if (!isMultiMode) return targetId;
    const t = (allPackets || []).find((p) => p.id === targetId);
    if (!t) return '';
    if (lockedClassificationId && t.classification_id !== lockedClassificationId) return '';
    return targetId;
  }, [targetId, isMultiMode, allPackets, lockedClassificationId]);

  // Multi-source can-submit guard (declared here so effectiveTargetId is in scope)
  const multiCanSubmit = (
    isMultiMode &&
    anyRowFilled &&
    allRowsValid &&
    (
      (targetMode === 'existing' && !!effectiveTargetId) ||
      (targetMode === 'new' && (
        newPacket.valuation_mode !== 'fixed' ||
        (Number(newPacket.fixed_rate_per_ct) || 0) > 0
      ))
    )
  );

  // ============================ Submit ============================
  const submitSingle = async () => {
    if (!singleSourcePacket) { toast.error('Pick a source packet'); return; }
    const ct = Number(carats) || 0;
    const pcs = Number(pieces) || 0;
    // Wave 3.49 Rule 1 + Rule 2.
    const ruleErr = validateCaratMovement({
      txnCarats: ct, txnPieces: pcs,
      availableCarats: singleSourcePacket.balance_carats || 0,
      availablePieces: singleSourcePacket.balance_pieces || 0,
      packetNo: singleSourcePacket.packet_no,
      context: 'packet transfer',
    });
    if (ruleErr) { toast.error(ruleErr); return; }

    let payload;
    if (targetMode === 'existing') {
      if (!targetId) { toast.error('Pick a target packet'); return; }
      payload = {
        target_mode: 'existing', target_packet_id: targetId,
        carats: ct, pieces: pcs, note,
      };
    } else {
      const cls_id = newPacket.classification_id || singleSourcePacket.classification_id;
      const valuation = (newPacket.valuation_mode || 'fixed').toLowerCase();
      if (valuation === 'fixed' && (Number(newPacket.fixed_rate_per_ct) || 0) <= 0) {
        toast.error('Fixed rate must be greater than 0');
        return;
      }
      payload = {
        target_mode: 'new',
        new_packet: {
          classification_id: cls_id,
          description: newPacket.description || '',
          valuation_mode: valuation,
          fixed_rate_per_ct: valuation === 'fixed' ? Number(newPacket.fixed_rate_per_ct) : null,
        },
        carats: ct, pieces: pcs, note,
      };
    }

    setSubmitting(true);
    try {
      const { data } = await api.post(`${meta.base}/packets/${singleSourcePacket.id}/transfer`, payload);
      const msg = targetMode === 'new'
        ? `Transferred to new packet ${data.target_packet?.packet_no || ''}`
        : `Transferred to ${data.target_packet?.packet_no || ''}`;
      toast.success(msg);
      onOpenChange(false);
      onTransferred && onTransferred(data);
    } catch (e) {
      toast.error(extractError(e, 'Transfer failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitMulti = async () => {
    if (!multiCanSubmit) {
      toast.error('Please fix the errors before submitting');
      return;
    }
    // Build payload
    const sources = validatedRows
      .filter((r) => r.valid && r.packet)
      .map((r) => ({
        packet_id: r.packet.id,
        carats: r.ct,
        pieces: r.pcs,
      }));

    if (sources.length === 0) {
      toast.error('Add at least one valid source packet');
      return;
    }

    let payload;
    if (targetMode === 'existing') {
      if (!effectiveTargetId) { toast.error('Pick a target packet'); return; }
      payload = { sources, target_mode: 'existing', target_packet_id: effectiveTargetId, note };
    } else {
      const valuation = (newPacket.valuation_mode || 'fixed').toLowerCase();
      if (valuation === 'fixed' && (Number(newPacket.fixed_rate_per_ct) || 0) <= 0) {
        toast.error('Fixed rate must be greater than 0');
        return;
      }
      payload = {
        sources,
        target_mode: 'new',
        new_packet: {
          classification_id: lockedClassificationId,
          description: newPacket.description || '',
          valuation_mode: valuation,
          fixed_rate_per_ct: valuation === 'fixed' ? Number(newPacket.fixed_rate_per_ct) : null,
        },
        note,
      };
    }

    setSubmitting(true);
    try {
      const { data } = await api.post(`${meta.base}/packets/transfer-multi`, payload);
      const tno = data.target_packet?.packet_no || '';
      const cnt = data.sources_legs?.length || sources.length;
      toast.success(`Merged ${cnt} source packets into ${tno}`);
      onOpenChange(false);
      onTransferred && onTransferred(data);
    } catch (e) {
      toast.error(extractError(e, 'Multi-source transfer failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Effective source packet for the target section (in multi-mode use any selected)
  const anySelectedSource = useMemo(() => {
    if (isMultiMode) {
      const r = validatedRows.find((vr) => vr.packet);
      return r ? r.packet : null;
    }
    return singleSourcePacket;
  }, [isMultiMode, validatedRows, singleSourcePacket]);

  if (!meta) return null;

  // ============================ Render ============================
  // Pick which mode-specific source-display helpers to use
  const lockedClsCode = lockedClassificationId
    ? (
      (classifications.find((c) => c.id === lockedClassificationId) || {}).schema_code
      || (sourceCandidates.find((p) => p.classification_id === lockedClassificationId) || {}).classification_schema
      || lockedClassificationId
    )
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-y-auto"
        data-testid={`packet-transfer-dialog-${meta.testidScope}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-[var(--ink-navy)]" />
            {isMultiMode ? 'Merge Multiple Packets' : 'Transfer Packet'} · {meta.label}
          </DialogTitle>
          <DialogDescription>
            {isMultiMode
              ? 'Combine stock from multiple purchased packets into a single target packet. All selected sources must share the same classification — atomic all-or-nothing transfer.'
              : 'Merge or move purchased stock from one packet to another. Approval packets cannot be used as a source.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* =================== SINGLE-SOURCE UI =================== */}
          {!isMultiMode && (
            <div>
              <Label className="text-xs">Source Packet</Label>
              <SearchableSelect
                items={sourceCandidates}
                value={sourceId}
                onValueChange={(v) => setSourceId(v || '')}
                itemToLabel={(p) => `${p.packet_no} · ${formatCarats(p.balance_carats)} ct / ${p.balance_pieces} pcs · ₹${p.current_rate_per_ct || 0}/ct`}
                itemToSubLabel={(p) => p.classification_shape || p.classification_schema || null}
                searchKeys={['packet_no', 'classification_shape', 'classification_schema', 'description']}
                placeholder={sourceCandidates.length === 0 ? 'No purchased packets with available balance' : 'Pick a purchased packet with balance'}
                searchPlaceholder="Search packet no / shape…"
                disabled={sourceCandidates.length === 0}
                testId="pt-source-select"
              />
              {singleSourcePacket && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Available: <span className="font-mono">{formatCarats(singleSourcePacket.balance_carats)} ct · {singleSourcePacket.balance_pieces} pcs</span>
                  {singleSourcePacket.purchase_status !== 'purchased' && (
                    <span className="ml-2 text-rose-700 font-medium">On Approval — cannot transfer</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* =================== MULTI-SOURCE UI =================== */}
          {isMultiMode && (
            <Card className="p-3 bg-white border-[var(--divider)]" data-testid="pt-multi-sources-card">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-[var(--ink-navy)]">Source Packets</Label>
                {lockedClsCode && (
                  <Badge variant="secondary" className="text-[10px]" data-testid="pt-locked-classification">
                    Classification locked: {lockedClsCode}
                  </Badge>
                )}
              </div>

              <div className="space-y-2">
                {validatedRows.map((row, idx) => {
                  const candidates = rowCandidates(row._rid);
                  return (
                    <div
                      key={row._rid}
                      className="rounded-md border border-[var(--divider)] bg-slate-50/40 p-2"
                      data-testid={`pt-source-row-${idx}`}
                    >
                      <div className="grid grid-cols-12 gap-2 items-start">
                        {/* Packet picker */}
                        <div className="col-span-12 md:col-span-6">
                          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Packet</Label>
                          <SearchableSelect
                            items={candidates}
                            value={row.packet_id || ''}
                            onValueChange={(v) => updateRow(row._rid, { packet_id: v || '' })}
                            itemToLabel={(p) => `${p.packet_no} · ${formatCarats(p.balance_carats)} ct / ${p.balance_pieces} pcs · ₹${p.current_rate_per_ct || 0}/ct`}
                            itemToSubLabel={(p) => p.classification_shape || p.classification_schema || null}
                            searchKeys={['packet_no', 'classification_shape', 'classification_schema', 'description']}
                            placeholder={
                              candidates.length === 0
                                ? (lockedClassificationId ? 'No more packets in this classification' : 'No purchased packets with balance')
                                : (lockedClassificationId ? `Pick packet (${lockedClsCode})` : 'Pick a purchased packet')
                            }
                            searchPlaceholder="Search packet no / shape…"
                            disabled={candidates.length === 0}
                            testId={`pt-source-select-${idx}`}
                          />
                        </div>
                        {/* Carats */}
                        <div className="col-span-5 md:col-span-2">
                          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Carats</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={row.carats}
                            onChange={(e) => updateRow(row._rid, { carats: e.target.value })}
                            placeholder="0.00"
                            data-testid={`pt-source-carats-${idx}`}
                          />
                        </div>
                        {/* Pieces */}
                        <div className="col-span-5 md:col-span-2">
                          <Label className="text-[10px] uppercase tracking-wide text-slate-500">Pieces</Label>
                          <Input
                            type="number"
                            step="1"
                            value={row.pieces}
                            onChange={(e) => updateRow(row._rid, { pieces: e.target.value })}
                            placeholder="0"
                            data-testid={`pt-source-pieces-${idx}`}
                          />
                        </div>
                        {/* Remove */}
                        <div className="col-span-2 md:col-span-2 flex items-end justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={sourceRows.length <= 1}
                            onClick={() => removeRow(row._rid)}
                            className="text-rose-700 hover:text-rose-900 hover:bg-rose-50"
                            data-testid={`pt-source-remove-${idx}`}
                            title={sourceRows.length <= 1 ? 'Need at least one row' : 'Remove this source'}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                      {/* Row info + errors */}
                      {row.packet && (
                        <div className="mt-1 text-[11px] flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="text-slate-500">
                            Available: <span className="font-mono">{formatCarats(row.packet.balance_carats)} ct · {row.packet.balance_pieces} pcs</span>
                          </span>
                          {row.errors.length > 0 && (
                            <span className="text-rose-700 font-medium" data-testid={`pt-source-error-${idx}`}>
                              {row.errors.join(' · ')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-2 flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addRow}
                  className="text-xs"
                  data-testid="pt-add-source-button"
                  disabled={
                    // Disable if there are no more eligible candidates left
                    lockedClassificationId &&
                    sourceCandidates.filter((p) =>
                      p.classification_id === lockedClassificationId &&
                      !selectedSourceIds.has(p.id)
                    ).length === 0
                  }
                >
                  <Plus size={12} className="mr-1" /> Add another source
                </Button>
                {totals.ct > 0 || totals.pcs > 0 ? (
                  <div className="text-[11px] text-slate-600">
                    Total: <span className="font-mono font-medium">{formatCarats(totals.ct)} ct · {totals.pcs} pcs</span>
                    <span className="ml-2 text-slate-400">≈ {formatINR(totals.value)}</span>
                  </div>
                ) : null}
              </div>
            </Card>
          )}

          {/* =================== TARGET PICKER (shared) =================== */}
          {anySelectedSource && (
            <Card className="p-3 bg-slate-50/40 border-[var(--divider)]">
              <Label className="text-xs">Target</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  className={`text-xs px-3 py-2 rounded border ${targetMode === 'existing'
                    ? 'border-[var(--ink-navy)] bg-white text-[var(--ink-navy)] font-medium'
                    : 'border-slate-300 text-slate-600 hover:border-slate-400'}`}
                  onClick={() => setTargetMode('existing')}
                  data-testid="pt-target-mode-existing"
                >
                  Existing Packet
                </button>
                <button
                  type="button"
                  className={`text-xs px-3 py-2 rounded border inline-flex items-center justify-center gap-1 ${targetMode === 'new'
                    ? 'border-[var(--ink-navy)] bg-white text-[var(--ink-navy)] font-medium'
                    : 'border-slate-300 text-slate-600 hover:border-slate-400'}`}
                  onClick={() => setTargetMode('new')}
                  data-testid="pt-target-mode-new"
                >
                  <Plus size={12} /> Create New Packet
                </button>
              </div>

              {targetMode === 'existing' && (
                <div className="mt-3">
                  <Label className="text-xs">Pick target packet (same classification, purchased only)</Label>
                  {(() => {
                    const targetItems = (isMultiMode ? multiTargetCandidates : singleTargetCandidates);
                    return (
                      <SearchableSelect
                        items={targetItems}
                        value={effectiveTargetId}
                        onValueChange={(v) => setTargetIdSafe(v || '')}
                        itemToLabel={(p) => `${p.packet_no} · ${(p.valuation_mode || 'fixed').replace('_', ' ')} · ${formatCarats(p.balance_carats)} ct`}
                        searchKeys={['packet_no', 'classification_shape', 'classification_schema', 'description']}
                        placeholder={targetItems.length === 0 ? 'No matching purchased packets — create a new one' : 'Choose destination packet'}
                        searchPlaceholder="Search packet no…"
                        disabled={targetItems.length === 0}
                        testId="pt-target-existing-select"
                      />
                    );
                  })()}
                </div>
              )}

              {targetMode === 'new' && (
                <div className="mt-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Classification</Label>
                      <Input
                        readOnly
                        value={
                          (classifications.find((c) => c.id === (anySelectedSource.classification_id)) || {}).schema_code ||
                          anySelectedSource.classification_schema || anySelectedSource.classification_id || ''
                        }
                        className="bg-slate-100 cursor-not-allowed"
                        data-testid="pt-new-classification"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Valuation Mode</Label>
                      <Select
                        value={newPacket.valuation_mode}
                        onValueChange={(v) => setNewPacket({ ...newPacket, valuation_mode: v })}
                      >
                        <SelectTrigger data-testid="pt-new-valuation-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fixed Rate</SelectItem>
                          <SelectItem value="running_average">Running Average</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {newPacket.valuation_mode === 'fixed' && (
                    <div>
                      <Label className="text-xs">Fixed Rate (₹ / ct)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newPacket.fixed_rate_per_ct}
                        onChange={(e) => setNewPacket({ ...newPacket, fixed_rate_per_ct: e.target.value })}
                        data-testid="pt-new-fixed-rate"
                      />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Description (optional)</Label>
                    <Input
                      value={newPacket.description}
                      onChange={(e) => setNewPacket({ ...newPacket, description: e.target.value })}
                      placeholder={isMultiMode
                        ? `Merged from ${sourceRows.filter((r) => r.packet_id).length} packets`
                        : `Merged packet from ${anySelectedSource.packet_no}`}
                      data-testid="pt-new-description"
                    />
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* =================== Single-source quantity row =================== */}
          {!isMultiMode && singleSourcePacket && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Carats</Label>
                <Input
                  type="number" step="0.01" value={carats}
                  onChange={(e) => setCarats(e.target.value)}
                  data-testid="pt-carats"
                />
              </div>
              <div>
                <Label className="text-xs">Pieces</Label>
                <Input
                  type="number" step="1" value={pieces}
                  onChange={(e) => setPieces(e.target.value)}
                  data-testid="pt-pieces"
                />
              </div>
              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. merged for stock simplification"
                  data-testid="pt-note"
                />
              </div>
            </div>
          )}

          {/* =================== Multi-source note row =================== */}
          {isMultiMode && anySelectedSource && (
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. merged for stock simplification"
                data-testid="pt-note"
              />
            </div>
          )}

          {/* =================== Single-source Preview =================== */}
          {!isMultiMode && singleSourcePacket && (Number(carats) > 0 || Number(pieces) > 0) && (
            <div className="rounded-md border border-[var(--divider)] bg-slate-50/40 p-3 text-xs">
              <div className="font-medium text-[var(--ink-navy)] mb-1">Preview</div>
              <div>
                <span className="text-slate-500">From source: </span>
                <span className="font-mono">{Number(carats) || 0} ct · {Number(pieces) || 0} pcs</span>
                <span className="ml-2 text-slate-500">@ ₹{singleSourcePacket.current_rate_per_ct || 0}/ct</span>
                <span className="ml-2 text-slate-500">= {formatINR((Number(carats) || 0) * (singleSourcePacket.current_rate_per_ct || 0))}</span>
              </div>
              <div className="mt-1 text-slate-500">
                Receiving packet handles value re-basis according to its valuation mode (fixed re-prices; running-average re-weights).
              </div>
            </div>
          )}

          {/* =================== Multi-source Preview =================== */}
          {isMultiMode && multiCanSubmit && (
            <div className="rounded-md border border-[var(--divider)] bg-slate-50/40 p-3 text-xs">
              <div className="font-medium text-[var(--ink-navy)] mb-1">Preview</div>
              <div>
                <span className="text-slate-500">Merging </span>
                <span className="font-mono">{validatedRows.filter((r) => r.valid && r.packet).length} packets</span>
                <span className="text-slate-500"> → </span>
                <span className="font-mono">{formatCarats(totals.ct)} ct · {totals.pcs} pcs</span>
                <span className="ml-2 text-slate-500">≈ {formatINR(totals.value)}</span>
              </div>
              <div className="mt-1 text-slate-500">
                Strictly atomic: if any source fails balance check at commit time, the entire batch is rolled back.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          {isMultiMode ? (
            <Button
              onClick={submitMulti}
              disabled={submitting || !multiCanSubmit}
              className="luxury-button"
              data-testid="pt-submit-button"
            >
              {submitting ? 'Merging…' : 'Merge Packets'}
            </Button>
          ) : (
            <Button
              onClick={submitSingle}
              disabled={submitting}
              className="luxury-button"
              data-testid="pt-submit-button"
            >
              {submitting ? 'Transferring…' : 'Transfer Stock'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
